import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import * as React from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { deepEqual } from 'jamespot-user-api';
import { DataCy } from '../../../../types/dataAttributes';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { CheckboxValue } from './JRCInputCheckbox.types';
import { RefCallBack } from 'react-hook-form';
import { FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import { PartialBy } from '../../../../types/utils';
import {
    CheckboxLabel,
    CheckboxOrToggle,
    DirectionContainer,
    FocusBackground,
} from '../Deprecated/JRCFormCheckbox/JRCFormCheckbox.style';
import { JRCRadio } from './components/JRCRadio';
import { useMemo } from 'react';

export const ReadOnlyBorder = styled.div`
    padding: 8px 16px;
    border: 1px solid ${(props) => props.theme.color.grey1};
    border-radius: 8px;
    min-height: 40px;
`;

type CheckboxOption<TFieldValues> = {
    label: string | JSX.Element;
    value: string | number | TFieldValues;
    disabled?: boolean;
    recommended?: boolean;
    description?: string;
};

export type CheckboxProps<TFieldValues> = DataCy & {
    disabled?: boolean;
    checkboxMode?: 'checkbox' | 'toggle' | 'radio';
    options: Array<CheckboxOption<TFieldValues>>;
    direction?: 'row' | 'column' | 'two-columns';
    variant?: 'default' | 'outlined';
    labelPosition?: 'before' | 'after';
    SortByChecked?: boolean;
    backgroundOnHover?: boolean;
    backgroundOnSelect?: boolean;
    valueTypeIsBoolean?: boolean;
    readOnly?: boolean;
    getReadOnlyLabel?: (value: TFieldValues) => React.ReactNode;
    limit?: number;
};

export type JRCInputCheckboxProps<TFieldValues extends FieldValues = FieldValues> = CheckboxProps<TFieldValues> &
    JRCInputFieldProps<TFieldValues>;
export type NativeCheckboxProps<TFieldValues extends FieldValues = FieldValues> = CheckboxProps<TFieldValues> &
    PartialBy<Omit<ControllerRenderProps<TFieldValues>, 'value' | 'ref'>, 'onBlur'> & {
        ref?: React.ForwardedRef<HTMLInputElement>;
    } & {
        value: TFieldValues | TFieldValues[];
        htmlFor?: string;
    };

export const ReadOnlyUl = styled.ul`
    margin-left: 20px;
`;
export const StyledDescription = styled.div`
    color: ${(props) => props.theme.color.grey4};
    font-size: 12px;
    margin-bottom: 16px;
`;
const InputCheckboxRaw = function <TFieldValues extends FieldValues = FieldValues>(
    { variant = 'default', ...props }: NativeCheckboxProps<TFieldValues>,
    ref?: React.ForwardedRef<HTMLInputElement>,
) {
    const intl = useIntl();

    const field = React.useMemo(
        () => ({
            onChange: props.onChange,
            onBlur: props.onBlur,
            value: props.value,
            name: props.name,
            ref: ref as RefCallBack,
        }),
        [props.name, props.value, props.onBlur, props.onChange, ref],
    );

    const [valueTypeIsBoolean, setValueTypeIsBoolean] = React.useState(
        props.options.length === 1 && props.valueTypeIsBoolean,
    );

    React.useEffect(() => {
        setValueTypeIsBoolean(props.options.length === 1 && props.valueTypeIsBoolean);
    }, [props.options.length, props.valueTypeIsBoolean]);

    const isChecked = React.useCallback(
        (
            field: PartialBy<Omit<ControllerRenderProps<TFieldValues>, 'value'>, 'onBlur'> & {
                value: TFieldValues | TFieldValues[];
            },
            option: CheckboxOption<TFieldValues>,
        ): boolean =>
            field &&
            (valueTypeIsBoolean
                ? !!field.value
                : props.checkboxMode === 'radio'
                ? deepEqual(field.value, option.value)
                : Array.isArray(field?.value) &&
                  (field?.value as CheckboxValue<TFieldValues>[])?.some((v) => deepEqual(v, option.value))),
        [valueTypeIsBoolean, props.checkboxMode],
    );

    const sortByCheck = React.useCallback(() => {
        const sortedOptions = [...props.options];
        return sortedOptions.sort((a, b) => {
            const aIndex = props.options.indexOf(a);
            const bIndex = props.options.indexOf(b);
            const bChecked = isChecked(field, b);
            const aChecked = isChecked(field, a);
            if (!bChecked && aChecked) {
                return -1;
            } else if (bChecked && !aChecked) {
                return 1;
            }
            return aIndex - bIndex;
        });
    }, [props.options, isChecked, field]);
    const [options, setOptions] = React.useState(props.SortByChecked ? sortByCheck() : props.options);

    React.useEffect(() => {
        //Updating options when length change
        setOptions(props.options);
    }, [props.options]);

    React.useEffect(() => {
        if (props.SortByChecked) setOptions(sortByCheck());
    }, [props.SortByChecked, sortByCheck]);

    const { getReadOnlyLabel: _getReadOnlyLabel } = props;
    const getReadOnlyLabel = React.useCallback(
        function (value: TFieldValues) {
            return _getReadOnlyLabel
                ? _getReadOnlyLabel(value)
                : options.find((option) => option.value === value)?.label;
        },
        [_getReadOnlyLabel, options],
    );
    const htmlFor = useMemo(
        () => (props.htmlFor ? `${props.htmlFor}-${props.name}` : props.name),
        [props.htmlFor, props.name],
    );

    return (
        <>
            {props.readOnly ? (
                <ReadOnlyBorder>
                    {Array.isArray(field.value) ? (
                        <ReadOnlyUl>
                            {field.value.map((value: TFieldValues, index) => (
                                <li key={index}>{getReadOnlyLabel(value)}</li>
                            ))}
                        </ReadOnlyUl>
                    ) : (
                        <JRCTypography size="l">{getReadOnlyLabel(field.value)}</JRCTypography>
                    )}
                </ReadOnlyBorder>
            ) : (
                <DirectionContainer direction={props.direction} variant={variant}>
                    {options.map((option, index) => {
                        const checked = isChecked(field, option);
                        return (
                            <div key={index}>
                                <CheckboxLabel
                                    style={{ marginBottom: option.description ? '0px' : '4px' }}
                                    direction={props.direction}
                                    labelPosition={props.labelPosition}
                                    variant={variant}
                                    disabled={props.disabled || option.disabled}
                                    checked={checked}
                                    backgroundOnHover={props.backgroundOnHover}
                                    backgroundOnSelect={props.backgroundOnSelect}
                                    htmlFor={`${htmlFor}-${option.value}`}>
                                    {props.checkboxMode === 'radio' ? (
                                        <>
                                            <JRCRadio
                                                name={props.name}
                                                ref={(index === 0 && ref) || undefined}
                                                id={`${htmlFor}-${option.value}`}
                                                data-cy={props.dataCy && `${props.dataCy}-option-${option.value}`}
                                                disabled={props.disabled || option.disabled}
                                                checked={checked}
                                                onChange={() => field.onChange(option.value ?? undefined)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <CheckboxOrToggle
                                                checkboxMode={props.checkboxMode}
                                                ref={(index === 0 && ref) || undefined}
                                                name={props.name}
                                                id={`${htmlFor}-${option.value}`}
                                                data-cy={props.dataCy && `${props.dataCy}-option-${option.value}`}
                                                disabled={
                                                    props.disabled ||
                                                    option.disabled ||
                                                    (field.value && props.limit === field.value.length && !checked)
                                                }
                                                checked={checked}
                                                onChange={(event) => {
                                                    if (valueTypeIsBoolean) {
                                                        return field.onChange(event.target.checked);
                                                    } else {
                                                        const newValue = [
                                                            ...((field?.value as TFieldValues[]) || []),
                                                        ] as Array<string | number | TFieldValues> | undefined;
                                                        if (event.target.checked) {
                                                            newValue?.push?.(option.value);
                                                        } else {
                                                            newValue?.splice?.(
                                                                newValue?.findIndex(
                                                                    (value) =>
                                                                        JSON.stringify(value) ===
                                                                        JSON.stringify(option.value),
                                                                ),
                                                                1,
                                                            );
                                                        }
                                                        return field.onChange(newValue);
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                                    <FocusBackground variant={variant} />
                                    {typeof option.label === 'string' && option.label.trim().length > 0
                                        ? intl.formatMessage({ id: option.label })
                                        : option.label}
                                    {option.recommended && (
                                        <>&nbsp;({intl.formatMessage({ id: 'GLOBAL_Recommended' })})</>
                                    )}
                                </CheckboxLabel>
                                {option.description && <StyledDescription>{option.description}</StyledDescription>}
                            </div>
                        );
                    })}
                </DirectionContainer>
            )}
        </>
    );
};
export const JRCInputCheckboxRaw = React.forwardRef(InputCheckboxRaw) as unknown as <
    T extends FieldValues = FieldValues,
>(
    props: NativeCheckboxProps<T>,
) => JSX.Element;

export function JRCInputCheckbox<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    rules,
    label,
    htmlFor,
    labelIsLegend,
    description,
    helper,
    ...props
}: JRCInputCheckboxProps<TFieldValues>) {
    return (
        <JRCFormFieldRenderer
            margin={props.margin}
            name={name}
            control={control}
            rules={rules}
            label={label}
            labelIsLegend={labelIsLegend || true}
            description={description}
            helper={helper}
            renderFunction={({ value, ...field }) => (
                <JRCInputCheckboxRaw {...props} htmlFor={htmlFor} {...field} value={value} />
            )}
        />
    );
}

JRCInputCheckbox.defaultProps = {
    direction: 'column',
    labelPosition: 'before',
    checkboxMode: 'checkbox',
    label: '',
    backgroundOnHover: false,
    backgroundOnSelect: false,
    SortByChecked: false,
    valueTypeIsBoolean: false,
};
